import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/libs';

import { useApp } from '../app-context';

import { IDENTIFICATION_TIMEOUT_MS } from './constants';
import type {
  IdentificationSettlement,
  IdentificationStatus,
  SettledIdentificationStatus
} from './types';
import { buildContextKey } from './utils';

type Settlement = {
  contextKey: string;
  status: SettledIdentificationStatus;
};

/**
 * Tracks how far LaunchDarkly has got with the evaluation context currently in
 * play.
 *
 * The context is derived here, during render, rather than reported by the
 * component that performs the identification. A consumer decides on the flag
 * value in the same render that the user or scope changes, so a signal that
 * only caught up in an effect would arrive after the decision it was meant to
 * hold — an effect-driven redirect commits in that very render's effect pass.
 */
const useIdentificationSettlement = (): IdentificationSettlement => {
  const { selectedRegion, selectedCropSeason } = useApp();
  const { user } = useAuth();

  const identificationContextKey = user
    ? buildContextKey({
        userId: user.id,
        selectedRegion,
        selectedCropSeason
      })
    : null;

  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [renderedContextKey, setRenderedContextKey] = useState(
    identificationContextKey
  );

  // Discarded on every transition, during render, so a consumer deciding in
  // this very render cannot act on it. Returning to a context settled earlier
  // has to wait for a fresh answer too: the identification for the context in
  // between may still be in flight and about to replace the flags.
  if (renderedContextKey !== identificationContextKey) {
    setRenderedContextKey(identificationContextKey);
    setSettlement(null);
  }

  // Mirrors the live key for the async settlement below, which runs long after
  // the render that produced its key.
  const currentContextKeyRef = useRef<string | null>(null);

  useEffect(() => {
    currentContextKeyRef.current = identificationContextKey;
  }, [identificationContextKey]);

  const settleIdentification = useCallback(
    (contextKey: string, status: SettledIdentificationStatus) => {
      setSettlement(current => {
        // A completion for a context that is no longer in play says nothing
        // about the one that is, and must not settle it.
        if (contextKey !== currentContextKeyRef.current) {
          return current;
        }

        if (current?.contextKey === contextKey && current.status === status) {
          return current;
        }

        return { contextKey, status };
      });
    },
    []
  );

  const currentSettlement =
    renderedContextKey === identificationContextKey &&
    settlement?.contextKey === identificationContextKey
      ? settlement
      : null;

  const identificationStatus: IdentificationStatus =
    identificationContextKey === null || currentSettlement === null
      ? 'pending'
      : currentSettlement.status;

  // Armed per context rather than once at mount. A deadline running before
  // there is anyone to identify would settle the wait on the anonymous
  // evaluation, and nothing could reopen it once the real user arrived.
  useEffect(() => {
    if (!identificationContextKey || identificationStatus !== 'pending') {
      return;
    }

    const timeoutId = setTimeout(
      () => settleIdentification(identificationContextKey, 'unevaluated'),
      IDENTIFICATION_TIMEOUT_MS
    );

    return () => clearTimeout(timeoutId);
  }, [identificationContextKey, identificationStatus, settleIdentification]);

  return {
    identificationStatus,
    identificationContextKey,
    settleIdentification
  };
};

export default useIdentificationSettlement;
